import React from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Grid, Link, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import * as yup from "yup";

import {ANGooglePlacesInput } from "@answersnow/pkg-theme";
import { formatToPhoneNumber } from "utils/formatFormFields";

const getValidationSchema = (t) =>
  yup.object().shape({
    firstName: yup.string().required(t("auth:updateMemberInfo.firstNameRequired")),
    lastName: yup.string().required(t("auth:updateMemberInfo.lastNameRequired")),
    phoneNumber: yup.string().required(t("auth:updateMemberInfo.phoneNumberRequired")),
  });

/**
 * The `NewTeamMemberForm` is a form component that allows users to input
 * information for a new team member such as first name, last name, email, phone number, and address.
 * @component
 * @param {Object} props The props of the component.
 * @param {string} props.email The email of the new team member.
 * @param {Function} props.onSubmit The function to call when the form is submitted.
 * @returns {JSX.Element} The `NewTeamMemberForm` component is being returned.
 */
function NewTeamMemberForm({ email, onSubmit, initialData }) {
  const { t } = useTranslation(["auth"]);

  // Formik configuration
  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isValid,
    isSubmitting,
    touched,
    values,
  } = useFormik({
    validationSchema: getValidationSchema(t),
    enableReinitialize: true,
    initialValues: {
      firstName: initialData?.firstName || "",
      lastName: initialData?.lastName || "",
      phoneNumber: initialData?.phoneNumber || "",
      address: {},
    },
    onSubmit: (values) => {
      return onSubmit(values);
    },
  });

  const handlePhoneNumberChange = (e) => {
    const phone = e.target.value.replace(/[^\d]/g, "");
    handleChange({ target: { name: "phoneNumber", value: phone } });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={7}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("auth:updateMemberInfo.firstNameLabel")}
            name="firstName"
            error={Boolean(touched.firstName && errors.firstName)}
            helperText={touched.firstName && errors.firstName}
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.firstName}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("auth:updateMemberInfo.lastNameLabel")}
            name="lastName"
            error={Boolean(touched.lastName && errors.lastName)}
            helperText={touched.lastName && errors.lastName}
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.lastName}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("auth:updateMemberInfo.emailLabel")}
            name="email"
            value={email}
            disabled
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("auth:updateMemberInfo.phoneNumberLabel")}
            name="phoneNumber"
            error={Boolean(touched.phoneNumber && errors.phoneNumber)}
            helperText={touched.phoneNumber && errors.phoneNumber}
            onBlur={handleBlur}
            onChange={handlePhoneNumberChange}
            value={formatToPhoneNumber(values.phoneNumber)}
          />
        </Grid>

        <Grid item xs={12}>
          <ANGooglePlacesInput
            fullWidth
            label={t("auth:updateMemberInfo.addressLabel")}
            name="address"
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2">
            {t("auth:updateMemberInfo.smsConsentPrefix")}{" "}
            <Link
              href="https://www.getanswersnow.com/privacy"
              underline="none"
              target="_blank"
              rel="noreferrer"
              color="info.main"
            >
              {t("auth:updateMemberInfo.privacyPolicy")}
            </Link>{" "}
            {t("auth:updateMemberInfo.smsConsentMiddle")}{" "}
            <Link
              href="https://www.getanswersnow.com/terms"
              underline="none"
              target="_blank"
              rel="noreferrer"
              color="info.main"
            >
              {t("auth:updateMemberInfo.termsOfService")}
            </Link>
            {t("auth:updateMemberInfo.smsConsentSuffix")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <LoadingButton
            fullWidth
            type="submit"
            loading={isSubmitting}
            disabled={!isValid}
          >
            {t("auth:updateMemberInfo.continueButton")}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
}

export default NewTeamMemberForm;
